import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Button, Col, Input, Select, Row, Modal, Radio } from "antd";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { HiArrowPath, HiOutlineViewColumns } from "react-icons/hi2";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import CommonModal from "../../../global/components/CommonModal";
import MultiSelect from "../../../global/components/inputWidgets/Transfer/MultiSelect";
import { FaColumns, FaRedo, FaUndoAlt, FaUser } from "react-icons/fa";
import { FaIdCard, FaLock } from "react-icons/fa6";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import {
  getDealerNo,
  getOemNo,
  getUserData,
  hasRole,
} from "../../../global/hooks/getUserRole";
import { TempUserForm } from "./TempUserModal";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { useNavigate } from "react-router-dom";

export const MySiteFilter = ({
  filter,
  handleFilterChange,
  handleFilter,
  handleReset,
  setShowModal,
  selectedRowKeys,
  setFilter,
  data,
  setColumnsValues,
}) => {
  const [showCommonModal, setShowCommonModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [cmdType, setCmdType] = useState(null);
  const loggedInUser = JSON.parse(sessionStorage.getItem("userData"));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateType, setUpdateType] = useState("update");
  // Add this after your existing useState declarations
  const [visibleColumns, setVisibleColumns] = useState([]);
  const initializedRef = useRef(false);
  const navigate = useNavigate();

  // Interactive progress modal state
  const [processedDevices, setProcessedDevices] = useState(0);
  const [totalDevices, setTotalDevices] = useState(0);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStoppedAtError, setIsStoppedAtError] = useState(false);
  const [completedErrorIndices, setCompletedErrorIndices] = useState(new Set());
  const [deviceResults, setDeviceResults] = useState([]);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const localColumnValues =
    JSON.parse(localStorage.getItem("column"))?.find(
      (item) => item?.user_id === getUserData()?.user_id,
    )?.column_data || {};

  // Get default columns based on role permissions
  const getDefaultColumns = useCallback(() => {
    // Default columns matching Columns.js structure
    const allDefaultColumns = [
      "DeviceNo",
      "DeviceName",
      "DeviceDesc",
      ...(hasRole(1, 2, 3) ? ["Oem"] : []),
      ...(hasRole(1, 2, 3, 4, 5, 6) ? ["Dealer"] : []),
      "Customer",
      "Mac",
      "SiteType",
      "Version",
      "Address",
      "Online",
    ];

    // Filter out Blocked and SiteKey as they're excluded from default visible columns
    return allDefaultColumns.filter(
      (item) => !["Blocked", "SiteKey"].includes(item),
    );
  }, []);

  // Generate column options from data
  const getColumnOptions = () => {
    if (!data?.data || data.data.length === 0) {
      // If no data, return default columns as options
      return getDefaultColumns().map((key) => ({
        id: key,
        value: key,
        label: key.replace(/([A-Z])/g, " $1").trim(),
      }));
    }

    const sampleObject = data.data[0];
    return Object.keys(sampleObject)
      .filter((key) => {
        // Role-based filtering for "Oem" column
        if (key === "Oem" && !hasRole(1, 2, 3)) {
          return false;
        }

        // Role-based filtering for "Dealer" column
        if (key === "Dealer" && !hasRole(1, 2, 3, 4, 5, 6)) {
          return false;
        }

        return true;
      })
      .map((key) => {
        if (key === "Online") {
          return {
            id: key,
            value: key,
            label: "Status",
          };
        }
        return {
          id: key,
          value: key,
          label: key.replace(/([A-Z])/g, " $1").trim(),
        };
      });
  };

  // Initialize with just column names
  useEffect(() => {
    // If we already have saved columns, use them
    if (localColumnValues?.Site) {
      if (!initializedRef.current) {
        setVisibleColumns(localColumnValues.Site);
        setColumnsValues(localColumnValues.Site);
        initializedRef.current = true;
      }
      return;
    }

    // If already initialized and no saved columns, don't reinitialize
    if (initializedRef.current) {
      return;
    }

    // Handle both cases: when data exists and when it doesn't
    let allColumnIds;

    if (data?.data && data.data.length > 0) {
      // Use columns from actual data
      allColumnIds = Object.keys(data.data[0])
        .filter((item) => !["Blocked", "SiteKey"].includes(item))
        .map((key, index) => key);
    } else {
      // No data available - use default columns
      allColumnIds = getDefaultColumns();
    }

    setVisibleColumns(allColumnIds);
    setColumnsValues(allColumnIds);
    initializedRef.current = true;
  }, [data, getDefaultColumns, localColumnValues]);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;
  const siteControlUrl = `${process.env.REACT_APP_API_URL}/api/sites/site-ctrl/`;
  const dealerOptionsUrl = filter?.OemNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${filter?.OemNo}/dealers/`
    : !hasRole(1, 2, 3) && getOemNo()
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${getOemNo()}/dealers/`
      : null;

  const custmerOptionsUrl = filter?.DealerNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/${filter?.DealerNo}/customers/`
    : !hasRole(1, 2, 3, 4, 5, 6) && getDealerNo()
      ? `${process.env.REACT_APP_API_URL}/api/dealers/${getDealerNo()}/customers/`
      : null;

  const {
    data: custmerOptions,
    loading: custmerOptionsLoading,
    error: custmerOptionsError,
  } = useGet(custmerOptionsUrl);

  const {
    data: optionsData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);

  const {
    data: postData,
    error: postError,
    loading: postLoading,
    postData: postAction,
  } = usePost(siteControlUrl);

  // Use refs to track processed responses and prevent duplicate processing
  const processedResponseRef = useRef(null);
  const processedDeviceResultsRef = useRef(null);

  const {
    data: dealerOptions,
    loading: dealerOptionsLoading,
    error: dealerOptionsError,
  } = useGet(dealerOptionsUrl);

  const modifiedSiteTypes = getModifiedResponse(optionsData, "sitetype");
  const modifiedOnlineStatus = getModifiedResponse(optionsData, "online");
  const modifiedOems = getModifiedResponse(optionsData, "oems");
  // const modifiedRMC = getModifiedResponse(optionsData, "rmc");
  const modifiedDealers = getModifiedResponse(dealerOptions, null);
  const modifiedCustomers = getModifiedResponse(custmerOptions, null);

  // Memoize selectedData to prevent unnecessary recalculations
  const selectedData = useMemo(() => {
    return (
      data?.data?.filter((item) => selectedRowKeys?.includes(item.DeviceNo)) ||
      []
    );
  }, [data?.data, selectedRowKeys]);

  // Memoize getErrorDevices to prevent unnecessary recalculations
  const getErrorDevices = useCallback(() => {
    if (!deviceResults || deviceResults.length === 0) return [];
    return deviceResults
      .map((result, index) => {
        // Match device by site_no (which should correspond to DeviceNo)
        const matchedDevice = selectedData?.find(
          (item) =>
            item.DeviceNo === result.site_no ||
            item.DeviceNo?.toString() === result.site_no?.toString(),
        );
        return {
          index,
          siteNo: result.site_no,
          deviceName: matchedDevice?.DeviceName || `Device ${result.site_no}`,
          success: result.success,
          output: result.output,
          error: result.error,
          result: result.result,
        };
      })
      .filter((item) => !item.success)
      .sort((a, b) => a.index - b.index);
  }, [deviceResults, selectedData]);

  // Find next uncompleted error index
  const getNextUncompletedErrorIndex = (errorDevicesList) => {
    for (let i = currentDeviceIndex; i < errorDevicesList.length; i++) {
      if (!completedErrorIndices.has(i)) {
        return i;
      }
    }
    return -1;
  };

  // Progress simulation effect
  useEffect(() => {
    if (!isProcessing || !totalDevices || isStoppedAtError) {
      return;
    }

    const errorDevicesList = getErrorDevices();
    const nextUncompletedErrorIndex =
      getNextUncompletedErrorIndex(errorDevicesList);
    let targetDevice;

    if (nextUncompletedErrorIndex >= 0) {
      targetDevice = errorDevicesList[nextUncompletedErrorIndex].index;
    } else {
      targetDevice = totalDevices; // Complete processing
    }

    // If we've reached the target
    if (processedDevices >= targetDevice) {
      if (nextUncompletedErrorIndex >= 0) {
        // Stop at error
        setCurrentDeviceIndex(nextUncompletedErrorIndex);
        setIsStoppedAtError(true);
        setIsProcessing(false);
      } else {
        // Complete processing
        setProcessedDevices(totalDevices);
        setIsProcessing(false);
        setIsStoppedAtError(false);
      }
      return;
    }

    // Continue progress simulation
    const baseDelay = 200;
    const extraDelayChance = Math.random() < 0.3;
    const delay = baseDelay + (extraDelayChance ? Math.random() * 600 : 0);

    const timeout = setTimeout(() => {
      setProcessedDevices((prev) => {
        const next = prev + 1;
        return next;
      });
    }, delay);

    return () => clearTimeout(timeout);
  }, [
    processedDevices,
    isProcessing,
    totalDevices,
    isStoppedAtError,
    currentDeviceIndex,
    completedErrorIndices,
    getErrorDevices,
  ]);

  // Handle initial results processing
  useEffect(() => {
    if (
      deviceResults &&
      deviceResults.length > 0 &&
      totalDevices > 0 &&
      showProgressModal
    ) {
      // Use JSON.stringify to track if we've processed these results
      const resultsKey = JSON.stringify(deviceResults);
      if (processedDeviceResultsRef.current === resultsKey) {
        return; // Already processed these results
      }
      processedDeviceResultsRef.current = resultsKey;

      // Calculate errors directly here instead of using getErrorDevices to avoid dependency issues
      const errors = deviceResults
        .map((result, index) => {
          const matchedDevice = selectedData?.find(
            (item) =>
              item.DeviceNo === result.site_no ||
              item.DeviceNo?.toString() === result.site_no?.toString(),
          );
          return {
            index,
            siteNo: result.site_no,
            deviceName: matchedDevice?.DeviceName || `Device ${result.site_no}`,
            success: result.success,
            output: result.output,
            error: result.error,
            result: result.result,
          };
        })
        .filter((item) => !item.success)
        .sort((a, b) => a.index - b.index);

      if (errors.length > 0) {
        // Has errors - start processing simulation to show them
        if (!isProcessing) {
          setProcessedDevices(0);
          setCurrentDeviceIndex(0);
          setCompletedErrorIndices(new Set());
          setIsProcessing(true);
          setIsStoppedAtError(false);
        }
      } else {
        // No errors - all succeeded, show success and auto-close
        setProcessedDevices(totalDevices);
        setIsProcessing(false);
        setIsStoppedAtError(false);
        openConfirmNotification("Success");
        setTimeout(() => {
          setShowProgressModal(false);
          handleStop();
        }, 2000);
      }
    }
  }, [
    deviceResults,
    totalDevices,
    showProgressModal,
    isProcessing,
    selectedData,
  ]);

  const onSearch = (value) => {
    console.log("search:", value);
  };

  // Handle temp user form success - show results in progress modal
  const handleTempUserSuccess = (responseData) => {
    setCmdType("Temp User");

    // Reset all state for fresh processing
    const deviceCount = selectedRowKeys?.length || 0;
    setTotalDevices(deviceCount);
    setProcessedDevices(0);
    setCurrentDeviceIndex(0);
    setCompletedErrorIndices(new Set());
    setShowAllErrors(false);
    setIsProcessing(false);
    setIsStoppedAtError(false);

    // Reset refs to allow processing new response
    processedResponseRef.current = null;
    processedDeviceResultsRef.current = null;

    // Open progress modal
    setShowProgressModal(true);

    // Set device results - the existing useEffect will handle processing
    if (
      responseData?.results &&
      Array.isArray(responseData.results) &&
      responseData.results.length > 0
    ) {
      setDeviceResults(responseData.results);
    } else {
      // No results array - treat as success and close
      openConfirmNotification("Success");
      setShowProgressModal(false);
      handleStop();
    }
  };

  const handleButtonClick = (type) => {
    // Show update modal for update action
    if (type === "update") {
      setShowUpdateModal(true);
      setUpdateType("update");
      return;
    }

    // Show confirmation modal for reboot, pw_reset, and license
    if (["reboot", "pw_reset", "license"].includes(type)) {
      setPendingAction(type);
      setShowConfirmModal(true);
      return;
    }

    // For other actions, proceed directly
    onSiteControl(type);
  };

  const handleUpdateConfirm = () => {
    setShowUpdateModal(false);
    // Send "update_all" if selected, otherwise "update"
    const actionType = updateType === "update_all" ? "update_all" : "update";
    onSiteControl(actionType);
  };

  const handleConfirmAction = () => {
    if (pendingAction) {
      setShowConfirmModal(false);
      onSiteControl(pendingAction);
      setPendingAction(null);
    }
  };

  const onSiteControl = async (type) => {
    // tmp_user opens form modal first, then shows summary modal on success
    if (type === "tmp_user") {
      // Reset all state when opening temp user modal to ensure fresh start
      setDeviceResults([]);
      setProcessedDevices(0);
      setCurrentDeviceIndex(0);
      setCompletedErrorIndices(new Set());
      setShowAllErrors(false);
      setIsProcessing(false);
      setIsStoppedAtError(false);
      processedResponseRef.current = null;
      processedDeviceResultsRef.current = null;
      setShowProgressModal(false);
      setIsModalOpen(true);
      return;
    }

    // All other buttons (update, reboot, pw_reset, license, oem_info) follow the same flow:
    // 1. Set command type
    // 2. Reset all state
    // 3. Open progress modal
    // 4. Make API call
    // 5. Show results in modal (only auto-close if all success: true)

    setCmdType(
      type === "oem_info"
        ? "OEM Info"
        : type === "pw_reset"
          ? "PW Reset"
          : type === "update_all"
            ? "Update All Nodes"
            : type,
    );

    // Reset state for new operation
    setTotalDevices(selectedRowKeys?.length || 0);
    setProcessedDevices(0);
    setCurrentDeviceIndex(0);
    setCompletedErrorIndices(new Set());
    setDeviceResults([]);
    setShowAllErrors(false);
    setIsProcessing(false);
    setIsStoppedAtError(false);

    // Reset the processed response refs for new request
    processedResponseRef.current = null;
    processedDeviceResultsRef.current = null;

    // Open progress modal FIRST - this must happen before API call
    setShowProgressModal(true);

    try {
      const modifiedData = { device_ids: selectedRowKeys, site_ctrl: type };
      await postAction(modifiedData);
    } catch (error) {
      console.error("Error in onSiteControl:", error);
      openErrorNotification("Something went wrong");
      setShowProgressModal(false);
      handleStop();
    }
  };

  // Handle continue processing
  const handleContinue = () => {
    // Mark current error as completed
    setCompletedErrorIndices((prev) => new Set([...prev, currentDeviceIndex]));

    // Start processing from next device
    setProcessedDevices((prev) => prev + 1);
    setIsStoppedAtError(false);
    setIsProcessing(true);
  };

  // Handle finish processing
  const handleFinish = () => {
    // Mark all remaining errors as completed and finish
    const errorDevicesList = getErrorDevices();
    const allIndices = errorDevicesList.map((_, idx) => idx);
    setCompletedErrorIndices(new Set(allIndices));
    setProcessedDevices(totalDevices);
    setIsStoppedAtError(false);
    setIsProcessing(false);

    // Close modal after a short delay
    setTimeout(() => {
      setShowProgressModal(false);
      // Reset state
      setProcessedDevices(0);
      setCurrentDeviceIndex(0);
      setCompletedErrorIndices(new Set());
      setDeviceResults([]);
    }, 1000);
  };

  // Handle stop processing
  const handleStop = () => {
    setShowProgressModal(false);
    setIsStoppedAtError(false);
    setIsProcessing(false);
    setCurrentDeviceIndex(0);
    setProcessedDevices(0);
    setCompletedErrorIndices(new Set());
    setDeviceResults([]);
    processedResponseRef.current = null;
    processedDeviceResultsRef.current = null;
  };

  useEffect(() => {
    // API always returns 200, so we check postError for actual HTTP errors
    if (postError) {
      const errorStatus = postError?.response?.status;
      const errorResults = postError?.response?.data?.results;

      if (
        errorStatus === 424 &&
        Array.isArray(errorResults) &&
        errorResults.length > 0
      ) {
        // Treat 424 responses with results similar to success responses
        const responseKey = JSON.stringify({
          results: errorResults,
          status: errorStatus,
        });
        if (processedResponseRef.current !== responseKey) {
          processedResponseRef.current = responseKey;
          processedDeviceResultsRef.current = null;
          setDeviceResults(errorResults);
          setShowProgressModal(true);
        }
        return;
      }

      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
      setShowProgressModal(false);
      handleStop();
      processedResponseRef.current = null;
      return;
    }

    // Check if we have postData (response received) and loading is complete
    // Since API always returns 200, we need to check the results field
    // Use ref to avoid processing the same response multiple times
    if (postData && !postLoading) {
      // Use JSON.stringify to compare objects properly
      const responseKey = JSON.stringify(postData);
      if (processedResponseRef.current === responseKey) {
        return; // Already processed this response
      }
      processedResponseRef.current = responseKey;

      // Check if response has results array
      if (
        postData?.results &&
        Array.isArray(postData.results) &&
        postData.results.length > 0
      ) {
        // Just set device results - the "Handle initial results processing" useEffect will handle the rest
        // Modal should already be open from onSiteControl, so don't set it again
        setDeviceResults(postData.results);
      } else {
        // No results array, assume success and close
        openConfirmNotification("Success");
        setShowProgressModal(false);
        handleStop();
      }
    }
  }, [postError, postData, postLoading]);
  // console.log(typeof filter.SiteType);

  return (
    <div className="">
      <CommonModal
        isVisible={showCommonModal}
        onClose={() => setShowCommonModal(false)}
        width={800}
        title="Column"
        icon={FaColumns}
        onConfirm={() => {
          setColumnsValues(visibleColumns);

          // Get existing data from localStorage
          const existingData = localStorage.getItem("column");
          let columnData = existingData ? JSON.parse(existingData) : [];

          const currentUserId = getUserData()?.user_id;

          // Find if current user already exists in the array
          const userIndex = columnData?.findIndex(
            (item) => item?.user_id === currentUserId,
          );

          if (userIndex !== -1) {
            // User exists - merge with existing column_data to preserve other pages
            columnData[userIndex].column_data = {
              ...columnData[userIndex].column_data, // Keep existing data (Device, Log, etc.)
              Site: visibleColumns, // Update only Site
            };
          } else {
            // User doesn't exist - create new entry
            columnData.push({
              user_id: currentUserId,
              column_data: { Site: visibleColumns },
            });
          }

          // Save updated data back to localStorage
          localStorage.setItem("column", JSON.stringify(columnData));

          setShowCommonModal(false);
        }}
        content={
          <MultiSelect
            name="Column"
            label={`Search`}
            value={visibleColumns}
            options={getColumnOptions()}
            onChange={(_, selected) => {
              setVisibleColumns(selected);
            }}
          />
        }
      />
      <div className="mt-2">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Input
              className="input_text_3 mr10"
              name="DeviceNo"
              placeholder="No"
              value={filter.DeviceNo}
              onChange={(e) => {
                const { value } = e.target;
                // Allow only positive integers or empty string
                if (/^[1-9]\d*$/.test(value) || value === "") {
                  handleFilterChange(e);
                }
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6} className="!h-[32px]">
            <Input
              className="input_text_3 mr10 !h-full"
              name="DeviceName"
              placeholder="Device Name"
              value={filter.DeviceName}
              onChange={handleFilterChange}
            />
          </Col>
          {hasRole(1, 2, 3) && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Select
                showSearch
                placeholder="OEM"
                optionFilterProp="label"
                value={
                  filter.OemNo || typeof filter.OemNo === "number"
                    ? Number(filter.OemNo)
                    : null
                }
                onChange={(value) => setFilter({ ...filter, OemNo: value })}
                onSearch={onSearch}
                options={modifiedOems}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
          )}

          {hasRole(1, 2, 3, 4, 5, 6) && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Select
                showSearch
                placeholder="Dealer"
                optionFilterProp="label"
                value={filter.DealerNo ? Number(filter.DealerNo) : undefined}
                onChange={(value) => setFilter({ ...filter, DealerNo: value })}
                onSearch={onSearch}
                options={modifiedDealers}
                style={{ width: "100%" }}
                disabled={
                  hasRole(4, 5, 6)
                    ? false
                    : filter.OemNo === undefined ||
                      filter.OemNo === null ||
                      filter.OemNo === ""
                }
                allowClear
                title="Please select an OEM first"
              />
            </Col>
          )}
          {!hasRole(9, 10, 11, 12) && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <Select
                showSearch
                placeholder="Customer"
                optionFilterProp="label"
                value={
                  filter.CustomerNo ? Number(filter?.CustomerNo) : undefined
                }
                onChange={(value) =>
                  setFilter({ ...filter, CustomerNo: value })
                }
                onSearch={onSearch}
                options={modifiedCustomers}
                style={{ width: "100%" }}
                disabled={
                  hasRole(7, 8, 9)
                    ? false
                    : filter.DealerNo === undefined ||
                      filter.DealerNo === null ||
                      filter.DealerNo === ""
                }
                allowClear
                title="Please select a Dealer first"
              />
            </Col>
          )}
          {/* <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Input
              className="input_text_3 mr10"
              name="Description"
              placeholder="Description"
              value={filter.Description}
              onChange={handleFilterChange}
            />
          </Col> */}
          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Site Type"
              optionFilterProp="label"
              value={
                filter.SiteType || filter.SiteType === 0
                  ? Number(filter.SiteType)
                  : undefined
              } //
              onChange={(value) => setFilter({ ...filter, SiteType: value })} //
              onSearch={onSearch}
              options={modifiedSiteTypes}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6} className="!h-[32px]">
            <Input
              className="input_text_3 mr10 !h-full"
              name="Mac"
              placeholder="MAC"
              value={filter.Mac}
              onChange={handleFilterChange}
            />
          </Col>
          {/* <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="RMC"
              optionFilterProp="label"
              value={filter.RMC ? Number(filter.RMC) : null} //
              onChange={(value) => setFilter({ ...filter, RMC: value })} //
              onSearch={onSearch}
              options={modifiedRMC}
              style={{ width: "100%" }}
              allowClear
            />
          </Col> */}
          <Col xs={24} sm={24} md={12} lg={6} xl={6} className="!h-[32px]">
            <Input
              className="input_text_3 mr10 !h-full"
              name="Version"
              placeholder="Version"
              value={filter.Version}
              onChange={handleFilterChange}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={6} className="!h-[32px]">
            <Input
              className="input_text_3 mr10 !h-full"
              name="Address"
              placeholder="Address"
              value={filter.Address}
              onChange={handleFilterChange}
            />
          </Col>

          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <Select
              showSearch
              placeholder="Status"
              optionFilterProp="label"
              value={
                filter.Online || typeof filter.Online === "number"
                  ? Number(filter.Online)
                  : null
              } //
              onChange={(value) => setFilter({ ...filter, Online: value })} //
              onSearch={onSearch}
              options={modifiedOnlineStatus?.map((item) => ({
                ...item,
                label: item.value === 1 ? "Online" : "Offline",
              }))}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
        </Row>
      </div>
      <div className="w-full overflow-x-auto">
        <div className="flex justify-between mt-4">
          <div className="flex items-center">
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium flex"
              onClick={handleFilter}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>

            <Button
              className="!mr-2 reset-button w-[104px] h-[30px] font-medium"
              onClick={handleReset}
            >
              <HiArrowPath className="!text-[14px] font-medium" />
              Reset
            </Button>
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium"
              onClick={() => setShowCommonModal(true)}
            >
              <HiOutlineViewColumns className="!text-[14px] font-medium" />
              Column
            </Button>
            {hasRole(1, 2) && (
              <Button
                type="primary"
                className="mr-2 w-[104px] h-[30px] font-medium flex"
                onClick={() => navigate("/site/add")}
              >
                <PlusOutlined className="!text-[14px] font-medium" />
                Add
              </Button>
            )}
            {hasRole(1, 2) && (
              <Button
                type="primary"
                className="mr-2 w-[104px] h-[30px] font-medium flex"
                onClick={() => {
                  setShowModal(true);
                }}
                disabled={selectedRowKeys?.length === 0 ? true : false}
              >
                <DeleteOutlined className="!text-[14px] font-medium" />
                Delete
              </Button>
            )}
          </div>
          {[1, 2, 3, 7, 8, 9].includes(loggedInUser?.role_value) ? (
            <div className="flex">
              {[
                {
                  label: "Update",
                  icon: <FaRedo className="text-xs" />,
                  action: "update",
                  disabled: !selectedData?.every((item) =>
                    ["RMC Plus"].includes(item.SiteType),
                  ),
                },
                {
                  label: "Reboot",
                  icon: <FaUndoAlt className="text-xs" />,
                  action: "reboot",
                  disabled: !selectedData?.every((item) =>
                    ["RMC Plus"].includes(item.SiteType),
                  ),
                },
                {
                  label: "PW Reset",
                  icon: <FaLock className="text-xs" />,
                  action: "pw_reset",
                  disabled: !selectedData?.every((item) =>
                    ["RMC Plus", "Cloud"].includes(item.SiteType),
                  ),
                },
                {
                  label: "Temp User",
                  icon: <FaUser className="text-xs" />,
                  action: "tmp_user",
                  disabled: !selectedData?.every((item) =>
                    ["RMC Plus", "Cloud"].includes(item.SiteType),
                  ),
                },
                {
                  label: "License",
                  icon: <FaIdCard className="text-xs" />,
                  action: "license",
                  // disabled: !selectedData?.every((item) =>
                  //   ["On Premise", "RMC Plus", "RMC"].includes(item.SiteType)
                  // ),
                },
              ].map((btn, index) => (
                <Button
                  key={btn.action}
                  type="primary"
                  className={`${
                    index !== 4 && "mr-2"
                  } w-[115px] h-[30px] py-1 px-[10px] font-medium flex`}
                  onClick={() => handleButtonClick(btn.action)}
                  disabled={selectedRowKeys?.length === 0 || btn.disabled}
                >
                  {btn.icon}
                  <span>{btn.label}</span>
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        // className="my-modal"
        closeIcon={false}
        footer={null}
        width={520}
        centered
        motion={false}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaUser className="text-[15px] text-black ml-1" />
            <span className="text-[14px] ml-2">Temp User</span>
          </div>
        }
        destroyOnClose
      >
        <TempUserForm
          setIsModalOpen={setIsModalOpen}
          selectedData={selectedData}
          onSuccess={handleTempUserSuccess}
          isModalOpen={isModalOpen}
        />
      </Modal>
      <CommonModal
        isVisible={showProgressModal}
        onClose={handleStop}
        width={600}
        title={
          deviceResults && deviceResults.length > 0
            ? `${cmdType} Results`
            : `${cmdType} Processing...`
        }
        noFooter={!isStoppedAtError}
        footerComp={
          isStoppedAtError
            ? (() => {
                const errorDevicesList = getErrorDevices();
                const remainingErrors =
                  errorDevicesList.length - completedErrorIndices.size;
                return (
                  <div className="flex justify-center mt-4 gap-4">
                    {remainingErrors <= 1 ? (
                      <Button
                        type="primary"
                        className="w-[104px] h-[30px] font-medium"
                        onClick={handleFinish}
                        disabled={isProcessing}
                      >
                        <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                        Finish
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        className="w-[114px] h-[30px] font-medium"
                        onClick={handleContinue}
                        disabled={isProcessing}
                      >
                        <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                        Continue
                      </Button>
                    )}
                    <Button onClick={handleStop} className="reset-button">
                      <CloseOutlined className="!text-[12px] font-medium" />
                      Close
                    </Button>
                  </div>
                );
              })()
            : null
        }
        content={
          <div className="w-full space-y-4">
            {/* Progress Bar */}
            {totalDevices > 0 && (
              <div className="w-full">
                <div className="relative h-3 rounded bg-gray-200 overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full transition-all duration-200 ease-out ${
                      isStoppedAtError ? "bg-red-500" : "bg-customColor"
                    }`}
                    style={{
                      width: `${(processedDevices / totalDevices) * 100}%`,
                    }}
                  />
                </div>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {isProcessing ? (
                    <>
                      Processing device {processedDevices} / {totalDevices}
                    </>
                  ) : isStoppedAtError ? (
                    (() => {
                      const errorDevicesList = getErrorDevices();
                      const currentError =
                        errorDevicesList[currentDeviceIndex] || null;
                      const remainingErrors =
                        errorDevicesList.length - completedErrorIndices.size;
                      return currentError ? (
                        <span className="text-red-600">
                          Stopped at site {currentError.index + 1} /{" "}
                          {totalDevices} ({remainingErrors} error
                          {remainingErrors !== 1 ? "s" : ""} remaining)
                        </span>
                      ) : null;
                    })()
                  ) : (
                    <>
                      Completed {processedDevices} / {totalDevices}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Current Error Display */}
            {isStoppedAtError &&
              (() => {
                const errorDevicesList = getErrorDevices();
                const currentError =
                  errorDevicesList[currentDeviceIndex] || null;
                return currentError ? (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">
                      Error in {currentError.deviceName} (Site No:{" "}
                      {currentError.siteNo})
                    </h4>
                    <div className="space-y-1">
                      <div className="text-sm text-red-700">
                        • {currentError.output || "Command execute failed"}
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}

            {/* Summary Display */}
            {deviceResults &&
              deviceResults.length > 0 &&
              (() => {
                const errorDevicesList = getErrorDevices();
                return (
                  <div className="border rounded shadow-sm p-3 w-full text-xs text-gray-500">
                    <div className="flex justify-between items-center">
                      <p>
                        <span>Total:</span> {totalDevices},{" "}
                        <span className="text-green-600">
                          Success:{" "}
                          {deviceResults.filter((r) => r.success).length}
                        </span>
                        ,{" "}
                        <span className="text-red-600">
                          Failed: {errorDevicesList.length}
                        </span>
                      </p>
                      {errorDevicesList.length > 0 && (
                        <button
                          onClick={() => setShowAllErrors(!showAllErrors)}
                          className="px-3 py-1 border rounded text-primary hover:bg-blue-50 text-xs"
                        >
                          {showAllErrors
                            ? "Hide Error List"
                            : "Show Error List"}
                        </button>
                      )}
                    </div>
                    {showAllErrors && errorDevicesList.length > 0 && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 space-y-2 max-h-60 overflow-y-auto">
                        {errorDevicesList.map((errorDevice, index) => (
                          <div key={index} className="text-red-700">
                            • <strong>{errorDevice.deviceName}</strong> (Site
                            No: {errorDevice.siteNo}):{" "}
                            {errorDevice.output || "Command execute failed"}
                            {errorDevice.error && ` - ${errorDevice.error}`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

            {/* Processing message when not stopped */}
            {!isStoppedAtError && isProcessing && (
              <div className="mb-2 text-sm text-gray-700">
                Please wait while the command is being applied to{" "}
                {selectedData?.map((item) => item?.DeviceName || "").join(", ")}
              </div>
            )}
          </div>
        }
      />
      <ConfirmModal
        showModal={showConfirmModal}
        setShowModal={setShowConfirmModal}
        onDeleteConfirm={handleConfirmAction}
        content={
          pendingAction === "reboot"
            ? "Do you want to reboot?"
            : pendingAction === "pw_reset"
              ? "Do you want to reset password?"
              : pendingAction === "license"
                ? "Do you want to apply license?"
                : "Do you want to proceed?"
        }
      />
      <Modal
        open={showUpdateModal}
        onCancel={() => setShowUpdateModal(false)}
        closable={false}
        footer={null}
        width={520}
        centered
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaRedo className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Update
            </span>
          </div>
        }
      >
        <div>
          <div className="bg-white rounded-[4px] overflow-hidden">
            <div className="px-2 pt-2 pb-3">
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <Radio.Group
                    value={updateType}
                    onChange={(e) => setUpdateType(e.target.value)}
                    className="w-full"
                  >
                    <div className="flex gap-3">
                      <Radio
                        value="update"
                        className="text-[14px] font-[Poppins]"
                      >
                        Update Server Node
                      </Radio>
                      <Radio
                        value="update_all"
                        className="text-[14px] font-[Poppins]"
                      >
                        Update All Nodes
                      </Radio>
                    </div>
                  </Radio.Group>
                </Col>
              </Row>
              <div className="flex justify-center space-x-2 mt-5">
                <Button
                  type="primary"
                  className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
                  onClick={handleUpdateConfirm}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  <span>Apply</span>
                </Button>
                <Button
                  className="reset-button"
                  onClick={() => setShowUpdateModal(false)}
                >
                  <CloseOutlined />
                  <span>Cancel</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
